/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { ArrowNextIcon } from '@coursera/cds-icons';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import type { AutocompleteMode } from 'bundles/search-common/constants/autocomplete';
import type { AutocompleteItems } from 'bundles/search-common/utils/autocomplete';
import { getAutocompleteEventingData } from 'bundles/search-common/utils/autocomplete';

import _t from 'i18n!nls/search-common';

const styles = {
  onboardingContainer: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding-top: var(--cds-spacing-100) 0;
  `,
  onboardingText: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
    flex: 1;
  `,
  onboardingLink: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-50);
    color: var(--cds-color-grey-975);
    font-weight: 500;
  `,
  arrowIcon: css`
    width: 16px;
    height: 16px;
  `,
};

type Props = {
  autocompleteState: AutocompleteMode;
  id: string;
  recentlyViewedItems: AutocompleteItems;
  suggestionIndex?: number;
  path?: string;
  groupId?: string;
};

const AutocompleteOnboardingOption = ({
  autocompleteState,
  id,
  recentlyViewedItems,
  suggestionIndex,
  path,
  groupId,
}: Props) => {
  const trackingData = getAutocompleteEventingData(id, autocompleteState, recentlyViewedItems, suggestionIndex);
  const trackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_autocomplete_suggestions',
    trackingData,
    [],
    { fullyVisible: true, initialDelay: 1000 }
  );

  return (
    <div ref={trackingRef} css={styles.onboardingContainer} data-group={groupId}>
      <TrackedDiv withVisibilityTracking requireFullyVisible trackingName="autocomplete_item">
        <div css={styles.onboardingText}>
          <span>{_t('Not sure where to start?')}</span>
          <span css={styles.onboardingLink}>
            {_t('Take a short quiz')}
            <ArrowNextIcon aria-hidden="true" css={styles.arrowIcon} />
          </span>
        </div>
      </TrackedDiv>
    </div>
  );
};

export default AutocompleteOnboardingOption;
